import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { LaunchpadItem, LaunchpadSubtask, TaskStatus } from './LaunchpadProgress.interface';
import { isLaunchpadItem, LaunchpadTask } from './launchpadItemState';

import useUpdateOperatorTaskStatus from 'services/operatorTasks/mutations/useUpdateOperatorTaskStatus';
import { invalidateOperatorTasksQueries } from 'services/operatorTasks/queries/useOperatorTasks/queryKeys';

/** Marks a not-yet-started task in progress, then hands it to the matching click handler. */
function useStartLaunchpadTask(
    onItemClick: (item: LaunchpadItem) => void,
    onSubtaskClick?: (subtask: LaunchpadSubtask) => void
) {
    const { mutate: updateTaskStatus } = useUpdateOperatorTaskStatus();
    const queryClient = useQueryClient();

    return useCallback(
        (task: LaunchpadTask | null) => {
            if (!task) {
                return;
            }

            // READY is treated like PENDING for backward compatibility.
            if (task.status === TaskStatus.PENDING || task.status === TaskStatus.READY) {
                updateTaskStatus(
                    { id: task.id, status: TaskStatus.IN_PROGRESS },
                    {
                        onSuccess: () => {
                            invalidateOperatorTasksQueries(queryClient);
                        },
                    }
                );
            }

            if (isLaunchpadItem(task)) {
                onItemClick(task);
            } else if (onSubtaskClick) {
                onSubtaskClick(task);
            }
        },
        [updateTaskStatus, queryClient, onItemClick, onSubtaskClick]
    );
}

export default useStartLaunchpadTask;
